/* ============================================================================
 * ff-search-enhance.js — progressive enhancement for Horizon's search modal.
 *
 *  1. Remembers submitted search terms in localStorage and shows them as
 *     "Recent" pill chips in the [data-ff-recent] container (rendered into
 *     search-modal.liquid) while the input is empty.
 *  2. Wraps the typed query in <mark> inside predictive-search result titles
 *     (.resource-card__title — the visible one; the visually-hidden duplicate
 *     carries a different class so it is never double-wrapped).
 *
 * Defensive by design: every hook feature-detects and fails silent.
 * ========================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'ff_recent_searches';
  var MAX_RECENT = 6;

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecent(term) {
    term = (term || '').trim();
    if (term.length < 2) return;
    try {
      var list = readRecent().filter(function (t) {
        return t.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      window.localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
    } catch (e) { /* storage unavailable — ignore */ }
  }

  /* ── 1. Save terms on submit ──────────────────────────────────────────── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('.predictive-search-form, form[action$="/search"]')) return;
    var input = form.querySelector('input[name="q"]');
    if (input) saveRecent(input.value);
  }, true);

  /* ── 2. Recent-searches chips ─────────────────────────────────────────── */
  function renderRecent(modal) {
    var container = modal.querySelector('[data-ff-recent]');
    var list = modal.querySelector('[data-ff-recent-list]');
    var input = modal.querySelector('input[name="q"]');
    if (!container || !list || !input) return;

    var terms = readRecent();
    if (!terms.length || input.value.trim() !== '') {
      container.hidden = true;
      return;
    }
    var signature = terms.join('|');
    if (list.getAttribute('data-ff-filled') !== signature) {
      list.textContent = '';
      terms.forEach(function (term) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'ff-search-recent__pill';
        a.href = '/search?q=' + encodeURIComponent(term);
        a.textContent = term;
        li.appendChild(a);
        list.appendChild(li);
      });
      list.setAttribute('data-ff-filled', signature);
    }
    container.hidden = false;
  }

  /* ── 3. <mark> highlight matched query text in result titles ──────────── */
  function highlight(modal) {
    var input = modal.querySelector('input[name="q"]');
    var query = input ? input.value.trim() : '';
    if (query.length < 2) return;
    var rx;
    try {
      rx = new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
    } catch (e) { return; }

    modal.querySelectorAll('.resource-card__title').forEach(function (el) {
      if (el.classList.contains('visually-hidden')) return;
      if (el.dataset.ffMarkedFor === query) return;
      var text = el.textContent;
      rx.lastIndex = 0;
      if (!rx.test(text)) return;
      rx.lastIndex = 0;
      el.innerHTML = text.replace(rx, '<mark>$1</mark>');
      el.dataset.ffMarkedFor = query;
    });
  }

  /* ── 4. "See all results" label — desktop carries the query (fix10) ────── */
  function seeAllLabel(modal) {
    var button = modal.querySelector('.predictive-search__search-button');
    var input = modal.querySelector('input[name="q"]');
    if (!button || !input) return;
    var query = input.value.trim();
    var label = query
      ? 'See all results for “' + query + '” →'
      : 'See all results';
    if (button.textContent.trim() !== label) button.textContent = label;
  }

  /* ── Wire observers once a search modal is in the DOM ─────────────────── */
  function initModal(modal) {
    if (modal.dataset.ffSearchInit === '1') return;
    modal.dataset.ffSearchInit = '1';

    var apply = function () {
      renderRecent(modal);
      highlight(modal);
      seeAllLabel(modal);
    };

    new MutationObserver(apply).observe(modal, { childList: true, subtree: true });

    var input = modal.querySelector('input[name="q"]');
    if (input) input.addEventListener('input', function () { renderRecent(modal); });

    apply();
  }

  function scan() {
    document.querySelectorAll('.search-modal, #search-modal').forEach(initModal);
  }

  if (document.readyState !== 'loading') scan();
  else document.addEventListener('DOMContentLoaded', scan);
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
